<script setup>
/* eslint-disable vue/custom-event-name-casing */
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import InlineEditField from 'dashboard/components-next/pipeline/inline/InlineEditField.vue';
import InlineSelectPill from 'dashboard/components-next/pipeline/inline/InlineSelectPill.vue';

const props = defineProps({
  conversationData: {
    type: Object,
    default: null,
  },
  contactAvatarUrl: {
    type: String,
    default: '',
  },
  contactName: {
    type: String,
    default: '',
  },
  cardTitle: {
    type: String,
    required: true,
  },
  showSubtitle: {
    type: Boolean,
    default: false,
  },
  currentStageName: {
    type: String,
    default: '',
  },
  hasLinkedConversation: {
    type: Boolean,
    default: false,
  },
  isAdmin: {
    type: Boolean,
    default: false,
  },
  item: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits([
  'close',
  'navigate-to-conversation',
  'send-message',
  'delete-card',
  'update-title',
  'update-priority',
  'request-mark-won',
  'request-mark-lost',
  'request-reopen',
  'open-related-items',
]);

const { t } = useI18n();

const showQuickActionsMenu = ref(false);
const dropdownRef = ref(null);

const handleClickOutside = e => {
  if (
    showQuickActionsMenu.value &&
    dropdownRef.value &&
    !dropdownRef.value.contains(e.target)
  ) {
    showQuickActionsMenu.value = false;
  }
};

onMounted(() => document.addEventListener('click', handleClickOutside));
onBeforeUnmount(() =>
  document.removeEventListener('click', handleClickOutside)
);

const handleNavigateToConversation = () => {
  showQuickActionsMenu.value = false;
  emit('navigate-to-conversation');
};

const handleSendMessage = () => {
  showQuickActionsMenu.value = false;
  emit('send-message');
};

const handleDeleteCard = () => {
  showQuickActionsMenu.value = false;
  emit('delete-card');
};

const handleOpenRelatedItems = () => {
  showQuickActionsMenu.value = false;
  emit('open-related-items');
};

// ── Title inline edit ────────────────────────────────────────────────────────

const onTitleSave = newValue => {
  emit('update-title', newValue);
};

// ── Priority pill ────────────────────────────────────────────────────────────

const priorityValue = computed(
  () => props.item?.item_details?.priority?.toLowerCase() || 'none'
);

const priorityOptions = computed(() => [
  {
    value: 'high',
    label: t('PIPELINE.PRIORITY_LABELS.HIGH'),
    colorClass: 'bg-ruby-3 text-ruby-11',
  },
  {
    value: 'medium',
    label: t('PIPELINE.PRIORITY_LABELS.MEDIUM'),
    colorClass: 'bg-amber-3 text-amber-11',
  },
  {
    value: 'low',
    label: t('PIPELINE.PRIORITY_LABELS.LOW'),
    colorClass: 'bg-n-alpha-1 text-n-slate-11',
  },
  {
    value: 'none',
    label: t('PIPELINE.PRIORITY_LABELS.NONE'),
    colorClass: 'bg-n-alpha-1 text-n-slate-11',
  },
]);

const onPriorityChange = (newValue, oldValue) => {
  emit('update-priority', newValue, oldValue);
};

// ── Status pill (presentational — derived from props.item, never two-way) ───

const dealStatus = computed(() => {
  if (props.item?.won_at) return 'won';
  if (props.item?.lost_at) return 'lost';
  return 'open';
});

const statusOptions = computed(() => [
  {
    value: 'open',
    label: t('PIPELINE.CARD.STATUS.OPEN'),
    colorClass: 'bg-n-alpha-1 text-n-slate-11',
  },
  {
    value: 'won',
    label: t('PIPELINE.CARD.STATUS.WON'),
    colorClass: 'bg-emerald-3 text-emerald-11',
  },
  {
    value: 'lost',
    label: t('PIPELINE.CARD.STATUS.LOST'),
    colorClass: 'bg-ruby-3 text-ruby-11',
  },
]);

const onStatusChange = (newValue, oldValue) => {
  if (newValue === 'won') {
    emit('request-mark-won');
  } else if (newValue === 'lost') {
    emit('request-mark-lost');
  } else if (
    newValue === 'open' &&
    (oldValue === 'won' || oldValue === 'lost')
  ) {
    emit('request-reopen');
  }
};
</script>

<template>
  <div class="bg-n-solid-2 border-b border-n-weak px-6 py-4">
    <div class="flex items-center justify-between">
      <div class="flex items-center gap-4 min-w-0 flex-1">
        <!-- Back button -->
        <button
          class="p-2 rounded-lg hover:bg-n-alpha-1 transition-colors flex-shrink-0"
          @click="$emit('close')"
        >
          <fluent-icon icon="chevron-left" size="20" class="text-n-slate-11" />
        </button>

        <!-- Contact Avatar -->
        <Avatar
          :src="contactAvatarUrl"
          :name="contactName || cardTitle"
          :size="40"
          rounded-full
          class="flex-shrink-0"
        />

        <!-- Title + pills -->
        <div class="min-w-0 flex-1">
          <!-- Inline editable title -->
          <InlineEditField
            :model-value="cardTitle"
            type="text"
            display-class="text-heading-2 text-n-slate-12 font-semibold"
            input-class="text-heading-2 font-semibold"
            :empty-label="t('PIPELINE.UNTITLED_ITEM')"
            @save="onTitleSave"
          />

          <!-- Subtitle: contact name -->
          <div v-if="showSubtitle" class="flex items-center gap-2 mt-1">
            <span class="text-sm text-n-slate-11">
              {{ contactName }}
            </span>
          </div>

          <!-- Pills row (priority only — status moved to right side) -->
          <div class="flex items-center gap-2 mt-2 flex-wrap">
            <InlineSelectPill
              :model-value="priorityValue"
              :options="priorityOptions"
              placement="bottom-start"
              @change="onPriorityChange"
            />
          </div>
        </div>
      </div>

      <div class="flex items-center gap-3 flex-shrink-0 ml-4">
        <!-- Status pill (prominent — replaces "Outros Itens" button position) -->
        <InlineSelectPill
          :model-value="dealStatus"
          :options="statusOptions"
          placement="bottom-end"
          @change="onStatusChange"
        />

        <!-- Go to Conversation Button -->
        <button
          v-if="hasLinkedConversation"
          class="flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg border border-n-weak text-n-slate-11 hover:bg-n-alpha-1 transition-colors"
          :title="t('PIPELINE.OPEN_CONVERSATION')"
          @click="handleNavigateToConversation"
        >
          <fluent-icon icon="chat" size="16" />
          <span class="hidden sm:inline">{{
            t('PIPELINE.OPEN_CONVERSATION')
          }}</span>
        </button>

        <!-- Stage Badge -->
        <span
          class="px-3 py-1 text-sm font-medium rounded-full bg-n-alpha-1 text-n-brand"
        >
          {{ currentStageName }}
        </span>

        <!-- Quick Actions Dropdown (overflow menu — edit removed) -->
        <div ref="dropdownRef" class="relative">
          <button
            class="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-n-brand rounded-lg hover:brightness-110 transition-colors"
            @click="showQuickActionsMenu = !showQuickActionsMenu"
          >
            {{ t('PIPELINE.QUICK_ACTIONS_TITLE') }}
            <fluent-icon icon="chevron-down" size="16" />
          </button>

          <!-- Dropdown Menu -->
          <div
            v-if="showQuickActionsMenu"
            class="absolute right-0 top-full mt-2 w-48 bg-n-solid-2 rounded-lg shadow-lg border border-n-weak py-1 z-50"
          >
            <button
              v-if="conversationData"
              class="w-full px-4 py-2 text-left text-sm text-n-slate-11 hover:bg-n-alpha-1 hover:text-n-slate-12 flex items-center gap-2"
              @click="handleNavigateToConversation"
            >
              <fluent-icon icon="chat" size="16" />
              {{ t('PIPELINE.OPEN_CONVERSATION') }}
            </button>
            <button
              class="w-full px-4 py-2 text-left text-sm text-n-slate-11 hover:bg-n-alpha-1 hover:text-n-slate-12 flex items-center gap-2"
              @click="handleSendMessage"
            >
              <fluent-icon icon="send" size="16" />
              {{ t('PIPELINE.SEND_MESSAGE.TITLE') }}
            </button>
            <button
              class="w-full px-4 py-2 text-left text-sm text-n-slate-11 hover:bg-n-alpha-1 hover:text-n-slate-12 flex items-center gap-2"
              @click="handleOpenRelatedItems"
            >
              <fluent-icon icon="list" size="16" />
              {{ t('PIPELINE.TABS.RELATED_ITEMS') }}
            </button>
            <button
              v-if="isAdmin"
              class="w-full px-4 py-2 text-left text-sm hover:bg-ruby-3 flex items-center gap-2 text-ruby-11"
              @click="handleDeleteCard"
            >
              <fluent-icon icon="delete" size="16" />
              {{ t('PIPELINE.CARD.DELETE') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
